import React, { useState, useEffect } from 'react';
import { 
    Box, Paper, Typography, TextField, Button, MenuItem, Select, 
    FormControl, InputLabel, Stack, Alert, Snackbar, CircularProgress,
    List, ListItem, ListItemText, Divider, Pagination, LinearProgress,
    IconButton, InputAdornment, Avatar, Badge
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next';
import { userService } from '../api';

const Profile = () => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [logs, setLogs] = useState([]);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        preferred_language: '',
        avatar: null
    });
    const [passwordData, setPasswordData] = useState({
        old_password: '',
        new_password: '',
        confirm_new_password: ''
    });
    const [successMsg, setSuccessMessage] = useState('');
    const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [passwordGeneralError, setPasswordGeneralError] = useState(''); // Specific for password change
    const [passwordErrors, setPasswordErrors] = useState({});
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Cargar perfil solo la primera vez o cuando sea necesario
                const profileRes = await userService.getProfile();
                setFormData({
                    first_name: profileRes.data.first_name,
                    last_name: profileRes.data.last_name,
                    preferred_language: profileRes.data.preferred_language,
                    avatar: profileRes.data.avatar
                });
                
                if (profileRes.data.preferred_language) {
                    i18n.changeLanguage(profileRes.data.preferred_language);
                }

                // Cargar la primera página de logs
                await loadLogs(1);
            } catch (err) {
                setError('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [i18n]); // Se ejecuta al montar el componente

    const loadLogs = async (pageNumber, size = pageSize) => {
        try {
            const res = await userService.getUserLogs(pageNumber, size);
            setLogs(res.data.results);
            // Calcular total de páginas basándose en el conteo total (count) y tamaño solicitado
            setTotalPages(Math.ceil(res.data.count / size));
            setPage(pageNumber);
        } catch (err) {
            console.error("Failed to fetch logs", err);
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return 0;
        let strength = 0;
        if (password.length >= 8) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;
        return strength; // Máximo 4
    };

    const getStrengthLabel = (strength) => {
        switch(strength) {
            case 1: return t('strength_very_weak');
            case 2: return t('strength_weak');
            case 3: return t('strength_medium');
            case 4: return t('strength_strong');
            default: return '';
        }
    };

    const getStrengthColor = (strength) => {
        if (strength <= 2) return 'error';
        if (strength === 3) return 'warning';
        return 'success';
    };

    const handlePageSizeChange = (event) => {
        const newSize = event.target.value;
        setPageSize(newSize);
        loadLogs(1, newSize);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAvatarChange = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            
            // 1. Validar Tipo de Archivo (Específico para imágenes de perfil)
            const extension = file.name.split('.').pop().toLowerCase();
            const allowedImageExtensions = ['jpg', 'jpeg', 'png'];
            
            if (!allowedImageExtensions.includes(extension)) {
                setError(t('err_file_type_invalid_image'));
                return;
            }

            // 2. Validar Tamaño (Máximo 5MB - Sincronizado con valid_file_size en Backend)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                setError(t('err_file_too_large', { max: 5 }));
                return;
            }

            setError(''); // Limpiar errores si pasa la validación

            try {
                const res = await userService.updateAvatar(file);
                setFormData({ ...formData, avatar: res.data.avatar });
                setSuccessMessage(t('profile_success'));
            } catch (err) {
                console.error("Error al subir avatar", err);
                setError("No se pudo cargar la imagen. Intente con un formato válido.");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataToSave = { ...formData };
            // Si el avatar es un string (la URL que ya existe), lo eliminamos del PATCH
            // para evitar que el ImageField de Django lance un error de validación.
            if (typeof dataToSave.avatar === 'string') {
                delete dataToSave.avatar;
            }
            await userService.updateProfile(dataToSave);
            i18n.changeLanguage(formData.preferred_language);
            setSuccessMessage(t('profile_success'));
        } catch (err) {
            setError('Failed to update profile');
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordGeneralError(''); // Clear specific password errors
        setPasswordErrors({}); // Clear field-specific errors
        setSuccessMessage(''); // Resetea el mensaje de éxito para forzar la re-aparición de la alerta
        
        const errors = {};
        if (!/[A-Z]/.test(passwordData.new_password)) {
            errors.new_password = "La contraseña debe tener al menos una mayúscula.";
        }
        if (!/[0-9]/.test(passwordData.new_password)) {
            errors.new_password = (errors.new_password ? errors.new_password + " " : "") + "La contraseña debe tener al menos un número.";
        }

        if (Object.keys(errors).length > 0) {
            setPasswordGeneralError("La nueva contraseña no cumple con los requisitos de seguridad.");
            setPasswordErrors(errors);
            return;
        }

        setPasswordErrors({});
        setIsPasswordSubmitting(true);
        try {
            await userService.changePassword(
                passwordData.old_password,
                passwordData.new_password,
                passwordData.confirm_new_password
            );
            setSuccessMessage(t('profile_password_success'));
            setPasswordData({ old_password: '', new_password: '', confirm_new_password: '' });
        } catch (err) {
            console.error("Error al cambiar la contraseña:", err.response?.data || err.message);
            if (err.response?.status === 400 && err.response.data) {
                setPasswordErrors(err.response.data); // Errores de validación (ej: clave incorrecta)
            } else {
                setPasswordGeneralError('Error al cambiar la contraseña. Verifique los datos o contacte a soporte.');
            }
        } finally {
            setIsPasswordSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
            <Paper sx={{ p: 4, borderRadius: 2, mb: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                            <IconButton 
                                color="primary" 
                                aria-label="upload picture" 
                                component="label"
                                sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f5f5f5' }, boxShadow: 2 }}
                            >
                                <input hidden accept="image/*" type="file" onChange={handleAvatarChange} />
                                <PhotoCamera />
                            </IconButton>
                        }
                    >
                        <Avatar 
                            src={formData.avatar?.startsWith('http') ? formData.avatar : `http://${window.location.hostname}:8000${formData.avatar}`} 
                            sx={{ width: 120, height: 120, border: '4px solid #fff', boxShadow: 3 }}
                        />
                    </Badge>
                    <Typography variant="h6" sx={{ mt: 2 }}>{formData.first_name} {formData.last_name}</Typography>
                </Box>

                <Typography variant="h5" gutterBottom fontWeight="bold" align="center">{t('profile_title')}</Typography>                
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <TextField label={t('profile_first_name')} name="first_name" fullWidth value={formData.first_name} onChange={handleChange} variant="outlined" />
                        <TextField label={t('profile_last_name')} name="last_name" fullWidth value={formData.last_name} onChange={handleChange} variant="outlined" />
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>{t('profile_language')}</InputLabel>
                            <Select name="preferred_language" value={formData.preferred_language} label={t('profile_language')} onChange={handleChange}>
                                <MenuItem value="en">English</MenuItem>
                                <MenuItem value="es">Español</MenuItem>
                            </Select>
                        </FormControl>
                        <Button type="submit" variant="contained" size="large" fullWidth sx={{ mt: 2 }}>{t('profile_save')}</Button>
                    </Stack>
                </form>
            </Paper>

            <Paper sx={{ p: 4, borderRadius: 2, mb: 4 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">Seguridad de la Cuenta</Typography>
                {/* Mensaje de error general para la sección de contraseña */}
                {passwordGeneralError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordGeneralError('')}>{passwordGeneralError}</Alert>}

                <form onSubmit={handlePasswordSubmit}>
                    <Stack spacing={2}>
                        <TextField 
                            label="Contraseña Actual" 
                            type={showOldPassword ? 'text' : 'password'} 
                            name="old_password" 
                            fullWidth 
                            value={passwordData.old_password} onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                            error={!!passwordErrors.old_password} helperText={passwordErrors.old_password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                                            {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }} />
                        <TextField 
                            label="Nueva Contraseña" 
                            type={showNewPassword ? 'text' : 'password'} 
                            name="new_password" 
                            fullWidth 
                            value={passwordData.new_password} onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                            error={!!passwordErrors.new_password} helperText={passwordErrors.new_password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }} />
                        
                        {passwordData.new_password && (
                            <Box sx={{ mb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                    <Typography variant="caption">{t('password_strength')}</Typography>
                                    <Typography variant="caption" fontWeight="bold" color={`${getStrengthColor(getPasswordStrength(passwordData.new_password))}.main`}>
                                        {getStrengthLabel(getPasswordStrength(passwordData.new_password))}
                                    </Typography>
                                </Stack>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={(getPasswordStrength(passwordData.new_password) / 4) * 100} 
                                    color={getStrengthColor(getPasswordStrength(passwordData.new_password))}
                                    sx={{ height: 6, borderRadius: 5 }}
                                />
                            </Box>
                        )}

                        <TextField 
                            label="Confirmar Nueva Contraseña" 
                            type={showConfirmPassword ? 'text' : 'password'} 
                            name="confirm_new_password" 
                            fullWidth 
                            value={passwordData.confirm_new_password} onChange={(e) => setPasswordData({...passwordData, confirm_new_password: e.target.value})}
                            error={!!passwordErrors.confirm_new_password} helperText={passwordErrors.confirm_new_password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }} />
                        <Button 
                            type="submit" 
                            variant="outlined" 
                            color="primary"
                            disabled={isPasswordSubmitting}
                        >
                            {isPasswordSubmitting ? <CircularProgress size={24} /> : 'Actualizar Contraseña'}
                        </Button>
                    </Stack>
                </form>
            </Paper>

            <Paper sx={{ p: 4, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">{t('profile_logs_title')}</Typography>
                {logs.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">{t('profile_no_logs')}</Typography>
                ) : (
                    <List>
                        {logs.map((log, index) => (
                            <React.Fragment key={log.id}>
                                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                    <ListItemText
                                        primary={log.action}
                                        secondary={
                                            <>
                                                <Typography component="span" variant="body2" color="textPrimary">
                                                    {new Date(log.created_at).toLocaleString()}
                                                </Typography>
                                                {` — ${log.details}`}
                                            </>
                                        }
                                    />
                                </ListItem>
                                {index < logs.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{t('profile_page_size')}</InputLabel>
                        <Select
                            value={pageSize}
                            label={t('profile_page_size')}
                            onChange={handlePageSizeChange}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                    {totalPages > 1 && (
                        <Pagination 
                            count={totalPages} 
                            page={page} 
                            onChange={(e, value) => loadLogs(value)} 
                            color="primary" 
                        />
                    )}
                </Box>
            </Paper>

            <Snackbar 
                open={!!successMsg} 
                autoHideDuration={5000} 
                onClose={() => setSuccessMessage('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" sx={{ width: '100%' }}>{successMsg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default Profile;